import AddIcon from "@mui/icons-material/Add";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import { Box, Button, Container, Grid2 as Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import TitlePosterOrCoverCard from "../../../../../Components/Cards/MediaCard/title-poster-or-cover-card";
import WishlistAddToCollectionCard from "../../../../../Components/Cards/WishlistAddToCollectionCard";
import EmptyListSlot from "../../../../../Components/Misc/EmptyListSlot";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import {
  assetService,
  cacheService,
  mediaService,
  setupService,
  thumbnailService,
} from "../../../../../Core/Api/services";
import { ThumbnailSize } from "../../../../../Core/Api/Thumbnail/thumbnail-api-service";
import { AclTypes } from "../../../../../Core/Api/Types/acl-types";
import { UploadCardFormatDto } from "../../../../../Core/Api/Types/db-format-infos";
import { DbObjectCollectionInfos } from "../../../../../Core/Api/Types/db-object-collection-infos";
import {
  GetObject,
  IsObjectLoading,
  RefreshCurrentObject,
  SetFetchedMediaFiles,
} from "../../../../../Redux/Reducers/redux-slice-asset";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/redux-store";
import { UserHasAcl } from "../../../../../Utils/helpers";
import useCollectionActions from "../../../../../Utils/Hooks/useCollectionActions";
import { FileLinkedType } from "../../../../../Core/Api/Types/file-linked-type";
import { ModalContext } from "../../../../../Components/Modals";
import ModalTitleSetPosterOrCover, {
  ModalTitleSetPosterOrCoverElementProps,
} from "../../../../../Components/Modals/ModalTitleSetPosterOrCover/modal-title-set-poster-or-cover";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import {
  AssetLinkedResourceType,
  TitleCoverResource,
  TitlePosterResource,
} from "../../../../../Core/Api/Types/asset-resources";
import AssetSingleMediaFilesContent from "../../InventoryAssetSingle/AssetSingleMediaFiles/asset-single-media-files-content";

function AdminTitleMediaAndCollections() {
  const { t } = useTranslation();
  const params = useParams();
  const titleId: number = parseInt(params.id!) || 0;
  const title = useAppSelector(GetObject);
  const { AddItemsToCollection } = useCollectionActions();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const dispatch = useAppDispatch();
  const isObjectLoading = useAppSelector(IsObjectLoading);

  const [collections, SetCollections] = useState<DbObjectCollectionInfos[]>([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [formats, setFormats] = useState<UploadCardFormatDto[]>([]);

  const userCanOverridePoster = UserHasAcl(AclTypes.aclTitleOverridePoster);
  const userCanOverrideCover = UserHasAcl(AclTypes.aclTitleOverrideCover);

  const posterFormat: UploadCardFormatDto | undefined = useMemo(() => {
    return formats.find((x) => x.linkedType === FileLinkedType.PosterTitleMain);
  }, [formats]);

  const coverFormat: UploadCardFormatDto | undefined = useMemo(() => {
    return formats.find((x) => x.linkedType === FileLinkedType.CoverTitleMain);
  }, [formats]);

  const computeVigent = (collection: DbObjectCollectionInfos): [string, string, string, string] => {
    let covers =
      collection?.objectIds != undefined && collection?.objectIds?.length > 4
        ? collection.objectIds
            ?.slice(0, -4)
            .map<string>((objectId) => thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small))
        : collection.objectIds?.map<string>((objectId) =>
            thumbnailService.GetObjectPosterUrl(objectId, ThumbnailSize.small)
          ) || [];
    return [covers[0] || "", covers[1] || "", covers[2] || "", covers[3] || ""];
  };

  const titlePosterResource: TitlePosterResource | null = useMemo(() => {
    return (
      (title?.linkedResources?.find((x) => x.type === AssetLinkedResourceType.TitlePoster) as TitlePosterResource) ||
      null
    );
  }, [title]);

  const titleCoverResource: TitleCoverResource | null = useMemo(() => {
    return (
      (title?.linkedResources?.find((x) => x.type === AssetLinkedResourceType.TitleCover) as TitleCoverResource) || null
    );
  }, [title]);

  const onPosterOrCoverAssignedSuccess = () => {
    cacheService.SetLastUpdatedMediaTimestamp(new Date().getTime());
    dispatch(RefreshCurrentObject());
  };

  const openSetPosterModal = () => {
    if (!openModal || !title) return;

    openModal<ModalTitleSetPosterOrCoverElementProps>(ModalTitleSetPosterOrCover, {
      mode: "poster",
      titleId: titleId,
      format: posterFormat,
      title: title,
      currentResource: titlePosterResource,
      onSuccess: onPosterOrCoverAssignedSuccess,
    });
  };

  const openSetCoverModal = () => {
    if (!openModal || !title) return;

    openModal<ModalTitleSetPosterOrCoverElementProps>(ModalTitleSetPosterOrCover, {
      mode: "cover",
      titleId: titleId,
      format: coverFormat,
      title: title,
      currentResource: titleCoverResource,
      onSuccess: onPosterOrCoverAssignedSuccess,
    });
  };

  const refreshMediaFiles = () => {
    mediaService.GetFilesByObjectId(titleId).then(function (response) {
      dispatch(SetFetchedMediaFiles(response.files || []));
    });
  };

  useEffect(() => {
    if (!title) return;

    refreshMediaFiles();

    setupService.GetFormatsByLayerAndTypeFromObject(title).then((formatsDto) => {
      setFormats(formatsDto);
    });
  }, [title]);

  useEffect(() => {
    if (titleId === 0) return;

    assetService.SearchCollectionByObjectIds([titleId]).then(function (data) {
      setLoadingCollections(false);
      SetCollections(data);
    });
  }, [titleId]);

  return (
    <>
      {(userCanOverridePoster || userCanOverrideCover) && (
        <Stack pb={2} my={2} spacing={2}>
          <Typography variant="h5">{t("page:body.titleArtworks")}</Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            width={!userCanOverridePoster || !userCanOverrideCover ? "50%" : "100%"}
          >
            {!isObjectLoading ? (
              <>
                {userCanOverridePoster && (
                  <TitlePosterOrCoverCard
                    mode="poster"
                    titleId={titleId}
                    isAssigned={titlePosterResource !== null}
                    onClickReplace={openSetPosterModal}
                  />
                )}
                {userCanOverrideCover && (
                  <TitlePosterOrCoverCard
                    mode="cover"
                    titleId={titleId}
                    isAssigned={titleCoverResource !== null}
                    onClickReplace={openSetCoverModal}
                  />
                )}
              </>
            ) : (
              <>
                <Skeleton variant="rounded" height={114} width="80%" />
                <Skeleton variant="rounded" height={114} width="80%" />
              </>
            )}
          </Stack>
        </Stack>
      )}

      <Box pb={2} mt={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5">{t("page:body.collectionsContainingTitle", { title: title?.name })}</Typography>
          {collections.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (title !== undefined) AddItemsToCollection([title]);
              }}
            >
              {t("button:addToAnotherCollections")}
            </Button>
          )}
        </Stack>

        {loadingCollections ? (
          <TableSkeleton />
        ) : collections.length > 0 ? (
          <Grid container spacing={2}>
            {collections.map((collection, index) => (
              <Grid size={6} key={index}>
                <WishlistAddToCollectionCard
                  fixedWidth={48}
                  id={collection.id}
                  key={collection.id}
                  covers={computeVigent(collection)}
                  nbTitles={0}
                  nbTotaleItem={collection.objectIds?.length || 0}
                  title={collection.name!}
                  small
                  direction="row"
                  selectMode={false}
                  clickable={false}
                  isDeletable
                  idToDelete={titleId}
                  callback={() => {
                    SetCollections(collections.filter((x) => x.id != collection.id));
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <EmptyListSlot
            title={t("component:emptyListSlot.title.itIsEmptyHere")}
            icon={<BookmarksOutlinedIcon />}
            desc={t("component:emptyListSlot.desc.noCollectionYet")}
            CTA={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (title !== undefined) AddItemsToCollection([title]);
                }}
              >
                {t("button:addToAnotherCollections")}
              </Button>
            }
          />
        )}
      </Box>
      <AssetSingleMediaFilesContent refreshMediaFiles={refreshMediaFiles} />
    </>
  );
}

export default AdminTitleMediaAndCollections;
